import { Link } from 'react-router-dom';
import { 
  Search, Users, Clock, Link as LinkIcon, Database, Activity,
  FileCheck, Lock, FileText, Building2, File, Briefcase,
  Shield, Folder, Scale, ArrowRight, Check,
  AlertTriangle, UserX, Timer, Brain, BarChart3, Target,
  Sparkles, ListTodo, Settings2, Upload, Zap, Eye,
  MessageSquare, TrendingUp, PieChart, Bell, UserCog, Command
} from 'lucide-react';
import Navigation from '@/components/marketing/Navigation';
import Footer from '@/components/marketing/Footer';
import AIInsightsSuite from '@/components/marketing/AIInsightsSuite';
import SEO, { StructuredData } from '@/components/SEO';

function PricingCard({ title, subtitle, pilotPrice, monthlyPrice, launchFee, clientCount, features, isCustom }) {
  return (
    <div className="bg-[#F5F1EB] p-6 flex flex-col h-full">
      <h3 className="text-xl font-medium text-[#1A1411] mb-1">{title}</h3>
      <p className="text-sm text-[#6B5B52] mb-6">{subtitle}</p>
      <div className="inline-flex items-center gap-3 bg-[#EDE6DB] rounded px-4 py-2 mb-4 w-fit">
        <span className="text-sm text-[#6B5B52]">Paid pilot<br />(credited):</span>
        <span className="text-lg font-medium text-[#A86A3A]">{pilotPrice}</span>
      </div>
      {!isCustom && <p className="text-xs text-[#9B8B7E] italic mb-4">The pilot fee is credited to the launch fee on rollout</p>}
      {isCustom ? (
        <>
          <p className="text-xs text-[#9B8B7E] italic mb-4">Contact us for pricing</p>
          <h4 className="text-4xl font-light text-[#A86A3A] mb-2">Custom</h4>
          <p className="text-sm text-[#9B8B7E] mb-6">Contact us for pricing</p>
        </>
      ) : (
        <>
          <div className="mb-2">
            <span className="text-4xl font-light text-[#1A1411]">${monthlyPrice.toLocaleString()}</span>
            <span className="text-sm text-[#6B5B52] ml-2">AUD / month</span>
          </div>
          <p className="text-sm text-[#6B5B52] mb-4">+ ${launchFee.toLocaleString()} AUD one time launch fee</p>
          {clientCount && <p className="text-sm text-[#6B5B52] mb-4">{clientCount}</p>}
        </>
      )}
      <div className="mb-6">
        <h4 className="font-medium text-[#1A1411] mb-3">Includes</h4>
        <ul className="space-y-2">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-[#6B5B52]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1A1411] mt-2 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-auto">
        <Link to="/pilot" className="block w-full py-3 px-6 border-2 border-[#6B5B52] text-[#6B5B52] text-center font-medium hover:bg-[#6B5B52] hover:text-white transition-colors">
          Request a pilot
        </Link>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, capabilities }) {
  return (
    <div className="bg-white border border-[#E8E4DF] p-6 hover:border-[#A86A3A]/40 hover:shadow-md transition-all duration-300 group">
      <div className="w-12 h-12 rounded-lg bg-[#A86A3A]/10 flex items-center justify-center mb-4 group-hover:bg-[#A86A3A]/20 transition-colors">
        <Icon className="w-6 h-6 text-[#A86A3A]" />
      </div>
      <h3 className="text-lg font-medium text-[#1A1411] mb-2">{title}</h3>
      <p className="text-sm text-[#6B5B52] leading-relaxed mb-4">{description}</p>
      <ul className="space-y-2">
        {capabilities.map((cap, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#A86A3A] mt-0.5 flex-shrink-0" />
            <span className="text-sm text-[#6B5B52]">{cap}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatBlock({ value, label }) {
  return (
    <div className="text-center">
      <p className="text-3xl lg:text-4xl font-light text-[#A86A3A]">{value}</p>
      <p className="text-sm text-[#6B5B52] mt-1">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const platformCapabilities = [
    {
      icon: Users,
      title: 'Client 360',
      description: 'A unified view of every household, individual, entity, adviser, and operational status in one place.',
      capabilities: [
        'Household and entity model mapping family groups to trusts, companies, and SMSFs',
        'Individual profiles with roles, contacts, and relationship mapping',
        'Adviser directory with specialisation tracking',
        'Operational status, key dates, and action items at a glance'
      ]
    },
    {
      icon: Target,
      title: 'Lead Pipeline & CRM',
      description: 'A full lead management pipeline with Kanban board, qualification tracking, and conversion analytics.',
      capabilities: [
        'Drag-and-drop Kanban board with customisable pipeline stages',
        'BANT qualification framework for structured lead assessment',
        'AI-powered lead scoring with confidence levels',
        'Activity timeline tracking every touchpoint and interaction'
      ]
    },
    {
      icon: Brain,
      title: 'AI Operator Agents',
      description: 'Three proactive AI agents that draft your day, prep your meetings, and flag compliance breaches before they hit.',
      capabilities: [
        'Morning Briefing Agent — daily priorities, watchlist, and win line tailored to you',
        'Meeting Prep Agent — one-page brief in 10 seconds, printable',
        'Compliance Watcher — flags overdue reviews, stale handovers, unverified advisers',
        'AI Chat, Lead Scoring, Anomaly Detection — embedded across the platform'
      ]
    },
    {
      icon: Briefcase,
      title: 'Service Line Workspaces',
      description: 'A dedicated workspace per service line — Family Office, Wealth, Tax, Tax Advisory, Philanthropy, Trustee/Estate/Legal.',
      capabilities: [
        'Active tasks, team members, and matching advisers in one view',
        'Recurring Reviews scheduler — Monthly, Quarterly, Semi-Annual, Annual',
        'Handover Log so context never gets lost between team members',
        'Service-line tagging on tasks and notes for fragmentation-proof ops'
      ]
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Deep operational analytics with pipeline funnels, revenue forecasting, and health scoring.',
      capabilities: [
        'Pipeline funnel with stage-by-stage conversion rates',
        'Revenue forecasting across recurring and one-off fees',
        'Adviser performance dashboards with workload distribution',
        'Client health scores with automated risk flagging'
      ]
    },
    {
      icon: FileText,
      title: 'Document Management',
      description: 'Securely upload, version, tag, and manage documents tied to each client relationship.',
      capabilities: [
        'File uploads with automatic cloud storage',
        'Document versioning with full change history',
        'Category-based organisation with custom tagging',
        'Direct download links with access-controlled sharing'
      ]
    },
    {
      icon: ListTodo,
      title: 'Task Management & Automation',
      description: 'Template-driven workflows with automation rules that trigger based on conditions you set.',
      capabilities: [
        'Task templates library for repeatable processes',
        'Automation rules engine with trigger-condition-action logic',
        'Due date tracking with overdue and upcoming windows',
        'Workflow stages tied to client lifecycle milestones'
      ]
    },
    {
      icon: UserCog,
      title: 'Internal Adviser Hub',
      description: 'A central directory of your team — partners, advisers, consultants — selectable across tasks, client lead/supporting roles, and assignments.',
      capabilities: [
        'Manually curated team roster with titles and specialities',
        'Service-line tagging so the right adviser surfaces in pickers',
        'One-click assign as Lead Adviser or Supporting Adviser on any client',
        'Powers task owner pickers and assignment workflows org-wide'
      ]
    },
    {
      icon: Eye,
      title: 'Family Principal Portal',
      description: 'A confidential, mobile-first read-only view for the family — net worth, key dates, family tree, document vault.',
      capabilities: [
        'Magic-link sign-in or password — your choice',
        'Beautiful net-worth hero card and family tree',
        'Read-only document vault — encrypted, audited, secure',
        'Branded experience that turns clients into advocates'
      ]
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description: 'Enterprise-grade access controls, audit trails, and compliance tools built for sensitive data.',
      capabilities: [
        'Role-based access with restricted household support',
        'Full audit trail of every view, edit, and export action',
        'Rate-limited authentication to prevent brute force',
        'Multi-tenant architecture — clean isolation per organisation'
      ]
    }
  ];

  const problemCards = [
    {
      icon: AlertTriangle,
      title: 'Scattered client information across systems',
      points: [
        'Key records are split across SharePoint, Google Drive, inboxes, file servers, and PDFs',
        'There is no consistent source of truth, no clear owner, and no review cadence',
        'Version confusion wastes time as teams reconcile what is current'
      ]
    },
    {
      icon: UserX,
      title: 'Key person risk when staff change',
      points: [
        'Knowledge lives in one person\'s head, inbox, or spreadsheets',
        'Onboarding takes weeks because people have to relearn where information lives',
        'Offboarding leaves gaps, broken links, and duplicated work'
      ]
    },
    {
      icon: Timer,
      title: 'Time lost searching and reconfirming',
      points: [
        '"Where is X?" requests interrupt senior staff every day',
        'Authorities, roles, and key dates are rechecked each quarter',
        'As household count rises, manual chasing grows with it'
      ]
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Connect and organise',
      description: 'Import your client data, link your existing sources, and let QIndex One structure everything into a unified Client 360 view.',
      icon: Database
    },
    {
      step: '02',
      title: 'Operate and grow',
      description: 'Run meetings with AI prep briefs, manage leads through your pipeline, track tasks and compliance dates, and monitor your practice analytics.',
      icon: Activity
    },
    {
      step: '03',
      title: 'Scale with confidence',
      description: 'Onboard new team members with clear permissions, automate repetitive workflows, and let AI surface the insights that matter.',
      icon: TrendingUp
    }
  ];

  const pricingPlans = [
    {
      title: 'Private Office',
      subtitle: 'Foundations - Single family office, 1 client',
      pilotPrice: '$3,000 AUD',
      monthlyPrice: 2000,
      launchFee: 6000,
      clientCount: null,
      features: [
        'Client 360 with individuals, entities, roles, and advisers',
        'Document management with versioning and tagging',
        'Task management with templates and automation',
        'AI Chat Assistant and Meeting Prep Briefs',
        'Analytics dashboard with health scoring',
        'Full audit trail and activity feed'
      ]
    },
    {
      title: 'Multi Family Office Essentials',
      subtitle: 'Portfolio - Up to 20 clients',
      pilotPrice: '$10,000 AUD',
      monthlyPrice: 7500,
      launchFee: 20000,
      clientCount: 'Includes up to 20 clients',
      features: [
        'Everything in Private Office, plus:',
        'Lead Pipeline with Kanban board and AI scoring',
        'Revenue forecasting and adviser performance analytics',
        'Anomaly Detection across your portfolio',
        'Business Administration with team permissions',
        'Priority support and quarterly health check'
      ]
    },
    {
      title: 'Multi Family Office Growth',
      subtitle: 'Institution - 21 to 50 clients',
      pilotPrice: '$18,750 AUD',
      monthlyPrice: 15000,
      launchFee: 37500,
      clientCount: 'Includes 21 to 50 clients',
      features: [
        'Everything in MFO Essentials, plus:',
        'Advanced search and Command Palette for power users',
        'Pipeline funnel analytics with conversion tracking',
        'Custom automation rules engine',
        'Enhanced reporting and saved searches',
        'Higher user allowance and faster support response'
      ]
    },
    {
      title: 'Enterprise and Advisory',
      subtitle: 'Bespoke - 50+ clients or advice firms (custom)',
      pilotPrice: 'Custom',
      isCustom: true,
      features: [
        'Everything in MFO Growth, plus:',
        'Custom workflow templates matched to your practice',
        'Bulk client onboarding and structured data migration',
        'Enhanced restricted household governance',
        'Staff training programme and structured handovers',
        'Dedicated support model and tailored rollout plan'
      ]
    }
  ];

  const homeStructuredData = [
    StructuredData.organization,
    StructuredData.website,
    StructuredData.softwareApplication,
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <SEO page="home" structuredData={homeStructuredData} />
      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Hero Text */}
            <div>
              <div className="w-12 h-1 bg-[#A86A3A] mb-8" />
              <h1 className="text-4xl lg:text-5xl font-light text-[#1A1411] leading-tight mb-6" data-testid="hero-heading">
                The complete operating system<br />
                <span className="font-medium">for client intelligence</span>
              </h1>
              <p className="text-lg text-[#6B5B52] mb-4">
                Client 360. Lead Pipeline. AI Insights. Analytics. Documents. All in one place.
              </p>
              <p className="text-[#6B5B52] leading-relaxed mb-8">
                QIndex One is a modern CRM platform built for family offices and advice firms. Manage client relationships, track your pipeline, automate workflows, and let AI surface the insights that matter — all with the discretion and security your clients expect.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/pilot" className="inline-flex items-center gap-2 px-6 py-3 bg-[#A86A3A] text-white font-medium rounded hover:bg-[#8B5730] transition-colors" data-testid="hero-request-pilot-btn">
                  Request a pilot <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-[#A86A3A] text-[#A86A3A] font-medium rounded hover:bg-[#A86A3A]/5 transition-colors" data-testid="hero-book-walkthrough-btn">
                  Book a walkthrough
                </Link>
              </div>
            </div>

            {/* Right: Feature Highlights */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#A86A3A]/10 to-[#D4A574]/20 rounded-3xl transform -rotate-2" />
                <div className="relative bg-white border border-[#E8E4DF] rounded-2xl p-8 shadow-lg">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: Users, label: 'Client 360', color: 'bg-[#A86A3A]/10 text-[#A86A3A]' },
                      { icon: Target, label: 'Lead Pipeline', color: 'bg-blue-50 text-blue-600' },
                      { icon: Brain, label: 'AI Insights', color: 'bg-violet-50 text-violet-600' },
                      { icon: BarChart3, label: 'Analytics', color: 'bg-emerald-50 text-emerald-600' },
                      { icon: FileText, label: 'Documents', color: 'bg-amber-50 text-amber-600' },
                      { icon: Zap, label: 'Automation', color: 'bg-orange-50 text-orange-600' },
                      { icon: UserCog, label: 'Admin Panel', color: 'bg-stone-100 text-stone-600' },
                      { icon: Shield, label: 'Security', color: 'bg-red-50 text-red-600' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#FAF8F5] transition-colors">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                          <item.icon className="w-5 h-5" />
                        </div>
                        <span className="text-sm font-medium text-[#1A1411]">{item.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#E8E4DF] flex items-center gap-2 text-sm text-[#6B5B52]">
                    <Command className="w-4 h-4" />
                    <span>Press <kbd className="px-1.5 py-0.5 rounded bg-[#F5F3F0] text-xs font-mono">Cmd+K</kbd> to search anything</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Insights Suite (Hero feature: live AI demo) */}
      <AIInsightsSuite />

      {/* Stats Bar */}
      <section className="border-y border-[#E8E4DF] py-12 bg-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatBlock value="10+" label="Integrated Modules" />
            <StatBlock value="3 AI" label="Operator Agents" />
            <StatBlock value="2" label="External Portals" />
            <StatBlock value="6" label="Service Lines" />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="w-12 h-1 bg-[#A86A3A] mb-8" />
            <h2 className="text-3xl font-light text-[#1A1411] mb-4">The problem is operational drag</h2>
            <p className="text-[#6B5B52]">
              Family offices and advice firms face three interconnected challenges that slow down operations and increase risk.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {problemCards.map((card, idx) => (
              <div key={idx} className="bg-white border border-[#E8E4DF] p-6 hover:border-[#A86A3A]/30 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#A86A3A]/10 flex items-center justify-center mb-4">
                  <card.icon className="w-6 h-6 text-[#A86A3A]" />
                </div>
                <h3 className="text-lg font-medium text-[#1A1411] mb-4">{card.title}</h3>
                <ul className="space-y-3">
                  {card.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#A86A3A] mt-2 flex-shrink-0" />
                      <span className="text-sm text-[#6B5B52] leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#FAF8F5] py-20 lg:py-24" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light text-[#1A1411] mb-4">How it works</h2>
            <p className="text-[#6B5B52] max-w-2xl mx-auto">
              From scattered information to a unified, AI-powered operating platform in three steps.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {howItWorks.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#A86A3A]/10 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-[#A86A3A]" />
                </div>
                <div className="text-sm font-medium text-[#A86A3A] mb-2">{step.step}</div>
                <h3 className="text-lg font-medium text-[#1A1411] mb-3">{step.title}</h3>
                <p className="text-sm text-[#6B5B52] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Capabilities - Full Feature Showcase */}
      <section className="py-20 lg:py-24" id="product">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="w-12 h-1 bg-[#A86A3A] mb-8" />
            <h2 className="text-3xl font-light text-[#1A1411] mb-4">Everything your practice needs</h2>
            <p className="text-[#6B5B52]">
              Ten integrated modules — including three AI operator agents and two external portals — that cover every aspect of running a modern family office.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {platformCapabilities.map((feature, idx) => (
              <FeatureCard key={idx} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* AI Deep Dive Section */}
      <section className="bg-[#1A1411] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-12 h-1 bg-[#A86A3A] mb-8" />
              <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
                AI that works<br /><span className="font-medium text-[#D4A574]">behind the scenes</span>
              </h2>
              <p className="text-[#A8A29E] leading-relaxed mb-8">
                QIndex One embeds AI intelligence directly into your daily workflows. No separate tools, no context switching. Your team gets smarter insights exactly where they need them.
              </p>
              <div className="space-y-4">
                {[
                  { icon: MessageSquare, text: 'Ask the AI Chat Assistant anything about your client data' },
                  { icon: FileCheck, text: 'Generate meeting prep briefs from client history in seconds' },
                  { icon: Sparkles, text: 'Automatically score leads based on qualification data' },
                  { icon: Bell, text: 'Detect portfolio anomalies before they become problems' },
                  { icon: ListTodo, text: 'Receive smart task suggestions based on client patterns' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded bg-[#A86A3A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-[#D4A574]" />
                    </div>
                    <span className="text-[#D4CEC6] text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-xs text-[#6B5B52]">
                AI features can be toggled on or off by your organisation owner at any time.
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="bg-[#2A2320] border border-[#3D3530] rounded-xl p-8 space-y-4">
                <div className="flex items-center gap-2 text-[#D4A574] text-sm font-medium mb-4">
                  <Brain className="w-5 h-5" /> AI Meeting Prep Brief
                </div>
                <div className="space-y-3 text-sm text-[#A8A29E]">
                  <div className="p-3 rounded bg-[#1A1411] border border-[#3D3530]">
                    <p className="text-[#D4CEC6] font-medium text-xs mb-1">Client Overview</p>
                    <p>Baker Family Group - 4 individuals, 3 entities, $12.4M AUM across managed accounts.</p>
                  </div>
                  <div className="p-3 rounded bg-[#1A1411] border border-[#3D3530]">
                    <p className="text-[#D4CEC6] font-medium text-xs mb-1">Key Discussion Points</p>
                    <p>Annual review overdue by 14 days. Trust deed variation pending signature. New grandchild may require estate plan update.</p>
                  </div>
                  <div className="p-3 rounded bg-[#1A1411] border border-[#3D3530]">
                    <p className="text-[#D4CEC6] font-medium text-xs mb-1">Risk Flags</p>
                    <p>Insurance policy renewal in 21 days. SMSF audit documentation incomplete.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#FAF8F5] border border-[#E8E4DF] p-5 rounded-lg">
                  <PieChart className="w-8 h-8 text-[#A86A3A] mb-3" />
                  <p className="text-2xl font-light text-[#1A1411]">$2.4M</p>
                  <p className="text-xs text-[#6B5B52]">Total Revenue Tracked</p>
                </div>
                <div className="bg-[#FAF8F5] border border-[#E8E4DF] p-5 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-emerald-600 mb-3" />
                  <p className="text-2xl font-light text-[#1A1411]">68%</p>
                  <p className="text-xs text-[#6B5B52]">Pipeline Conversion</p>
                </div>
                <div className="bg-[#FAF8F5] border border-[#E8E4DF] p-5 rounded-lg">
                  <Activity className="w-8 h-8 text-blue-600 mb-3" />
                  <p className="text-2xl font-light text-[#1A1411]">94</p>
                  <p className="text-xs text-[#6B5B52]">Client Health Score</p>
                </div>
                <div className="bg-[#FAF8F5] border border-[#E8E4DF] p-5 rounded-lg">
                  <Eye className="w-8 h-8 text-violet-600 mb-3" />
                  <p className="text-2xl font-light text-[#1A1411]">Real-time</p>
                  <p className="text-xs text-[#6B5B52]">Adviser Performance</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="w-12 h-1 bg-[#A86A3A] mb-8" />
              <h2 className="text-3xl font-light text-[#1A1411] mb-4">Analytics that drive decisions</h2>
              <p className="text-[#6B5B52] leading-relaxed mb-6">
                Stop guessing and start knowing. QIndex One provides real-time analytics across your pipeline, revenue, team performance, and client health — all from a single dashboard.
              </p>
              <ul className="space-y-3">
                {[
                  'Pipeline funnel with stage-by-stage conversion tracking',
                  'Revenue forecasting across recurring and one-off income',
                  'Client health scoring with automated risk alerts',
                  'Adviser workload and performance benchmarking'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#A86A3A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#6B5B52]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Controls */}
      <section className="bg-[#FAF8F5] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-[#1A1411] mb-4">Built for teams that handle sensitive information</h2>
            <p className="text-[#6B5B52] max-w-2xl mx-auto">
              Enterprise-grade security with the flexibility to control exactly who sees what across your organisation.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Access control',
                items: ['Role-based permissions (Owner, Admin, Member)', 'Per-user client and pipeline access', 'Restricted household support', 'Organisation-wide feature toggles']
              },
              {
                title: 'Auditability',
                items: ['Complete access and edit logging', 'Change history on all records', 'Activity timeline per client', 'Exportable audit reports']
              },
              {
                title: 'Infrastructure',
                items: ['Rate-limited authentication endpoints', 'Secure cloud document storage', 'JWT-based session management', 'MongoDB with indexed queries']
              }
            ].map((column, idx) => (
              <div key={idx} className="bg-white border border-[#E8E4DF] p-6">
                <h3 className="text-lg font-medium text-[#1A1411] mb-4">{column.title}</h3>
                <ul className="space-y-3">
                  {column.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-[#A86A3A] flex-shrink-0" />
                      <span className="text-sm text-[#6B5B52]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pilot Targets */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-12 h-1 bg-[#A86A3A] mb-8" />
              <h2 className="text-3xl font-light text-[#1A1411] mb-4">Pilot targets</h2>
              <p className="text-[#6B5B52] mb-8">
                What a successful QIndex One pilot typically achieves within 4-6 weeks.
              </p>
              <ul className="space-y-4">
                {[
                  'Reduce time to find client information from ~6 minutes to ~2 minutes',
                  'Reduce new staff onboarding time from ~4 weeks to ~2 weeks',
                  'Cut "where is X?" requests by more than 60%',
                  'Reduce version control errors by more than 50%',
                  'Improve offboarding confidence with structured handovers'
                ].map((target, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#A86A3A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#6B5B52]">{target}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#FAF8F5] border border-[#E8E4DF] p-8">
              <h3 className="text-xl font-medium text-[#1A1411] mb-4">Ready to see the difference?</h3>
              <p className="text-[#6B5B52] mb-6">
                Start with a 4-6 week pilot on 1-3 client households. Experience the full platform — Client 360, AI insights, analytics, and automation — before committing.
              </p>
              <Link to="/pilot" className="inline-flex items-center gap-2 px-6 py-3 bg-[#A86A3A] text-white font-medium rounded hover:bg-[#8B5730] transition-colors">
                Request a pilot <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-[#FAF8F5] py-20 lg:py-24" id="pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-[#1A1411] mb-4">Simple, transparent pricing</h2>
            <p className="text-[#6B5B52]">
              Choose the plan that fits your organisation. All pilots are credited toward your first year.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {pricingPlans.map((plan, idx) => (
              <PricingCard key={idx} {...plan} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1A1411] py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-white mb-4">
            Ready to modernise your practice?
          </h2>
          <p className="text-[#A8A29E] mb-8">
            Client 360. Lead Pipeline. AI Intelligence. Analytics. All in one platform built for discretion.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/pilot" className="inline-flex items-center gap-2 px-6 py-3 bg-[#A86A3A] text-white font-medium rounded hover:bg-[#8B5730] transition-colors">
              Request a pilot <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded hover:bg-white/10 transition-colors">
              Book a walkthrough
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
